import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import type { MapsCandidate, MapsContactCandidateStatus } from '../../models/maps.types';
import { FormatContactStatusChip, IsContactStatusFailure } from '../../utils/contact-status';

/** Decorative header line-art variants from the design boilerplate; cards
 *  alternate by grid index. Bound via [attr.d] (no Angular security context). */
const CARD_LINE_VARIANTS: readonly (readonly [string, string, string])[] = [
    [
        'M 0 76 C 160 46, 340 94, 640 58',
        'M 0 95 C 220 66, 420 110, 640 82',
        'M 0 32 C 260 58, 440 18, 640 34',
    ],
    [
        'M 0 58 C 300 22, 480 88, 640 76',
        'M 0 82 C 180 112, 460 64, 640 95',
        'M 0 34 C 200 54, 440 14, 640 32',
    ],
];

/**
 * One search-result card. STN rebuild (client mockup, 2026-08): identity →
 * WHY THEY FIT (Show more → experience drawer) → RECENT ROLES → identity
 * links → Contact / View Details. Removed from the card (all still on the
 * wire for export/sort/drawer): match-tier badge, career-stage chip,
 * recruiting-feasibility line, Most Relevant Work + hover preview, Things to
 * Look Into (drawer-only now), View Profile.
 */
@Component({
    selector: 'maps-result-card',
    standalone: false,
    templateUrl: './result-card.component.html',
    styleUrl: './result-card.component.scss',
})
export class ResultCardComponent implements OnChanges {
    @Input({ required: true }) candidate!: MapsCandidate;
    @Input() isSelected = false;
    /** Contact rollup for this candidate (null = never contacted). Drives the
     *  "Contacted <date> · <engagement>" chip under the location line. */
    @Input() contactStatus: MapsContactCandidateStatus | null = null;
    /** Grid position — selects the decorative header line-art variant. */
    @Input() variantIndex = 0;
    /** True when summary generation gave up — the summary boxes swap their
     *  skeletons for a retry affordance. */
    @Input() summariesFailed = false;
    @Output() selectionChanged = new EventEmitter<{ candidateId: string; selected: boolean }>();
    /** Bubbles the summary-box Retry click up to the search page (which owns
     *  the mapsSummariesGenerate call). */
    @Output() summariesRetry = new EventEmitter<void>();
    /** Open the experience drawer for this candidate (View Details button,
     *  WHY THEY FIT "Show more", and the "+ N earlier roles" link). */
    @Output() viewExperience = new EventEmitter<void>();
    /** Open the contact modal for this candidate (Contact button — the mailto
     *  link was replaced by the modal, client direction 2026-08-11). */
    @Output() contact = new EventEmitter<void>();

    /** Pre-formatted chip text (NG0100: never a method call in the binding). */
    contactStatusText: string | null = null;
    contactStatusIsFailure = false;

    ngOnChanges(): void {
        this.contactStatusText = this.contactStatus !== null ? FormatContactStatusChip(this.contactStatus) : null;
        this.contactStatusIsFailure = this.contactStatus !== null && IsContactStatusFailure(this.contactStatus.lastStatus);
    }

    get displayName(): string {
        return `${this.candidate.firstName} ${this.candidate.lastName}`.trim();
    }

    get headerLines(): readonly [string, string, string] {
        return CARD_LINE_VARIANTS[this.variantIndex % CARD_LINE_VARIANTS.length];
    }

    get locationText(): string | null {
        const loc = this.candidate.location;
        if (!loc) return null;
        const parts = [loc.city, loc.state, loc.country].filter((p): p is string => !!p);
        return parts.length > 0 ? parts.join(', ') : null;
    }

    OnSelectionToggle(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.selectionChanged.emit({
            candidateId: this.candidate.candidateId,
            selected: input.checked,
        });
    }
}
